import { ReactNode, useEffect } from "react"
import { useCloseCorteAvailableSubscription, useGetHotelStatusQuery } from "src/gql/schema"
import { useProfile } from "../hooks/useProfile"
import { useDispatch } from "react-redux"
import { setHotelLoading, setModulos, toggleImasStatusAvailable } from "src/store/hotel/hotelSlice"

// Cada cuánto se reintenta mientras la query de estado del hotel no haya respondido.
const RETRY_INTERVAL = 15_000

const HotelImasStatusProvider = ({ children }: { children: ReactNode }) => {
    const { hotel_id } = useProfile()
    const dispatch = useDispatch()

    const { data } = useCloseCorteAvailableSubscription({
        variables: {
            hotel_id,
        },
    })

    const {
        data: hotelQueryData,
        error,
        refetch,
        stopPolling,
    } = useGetHotelStatusQuery({
        variables: {
            hotel_id,
        },
        skip: !hotel_id,
        // Mientras no lleguen los módulos, hotel.loading mantiene oculta la barra de
        // navegación, así que se reintenta en lugar de esperar indefinidamente: si la
        // query falla (red, timeout) no hay un segundo intento por sí solo.
        pollInterval: RETRY_INTERVAL,
    })

    const handleModulos = () => {
        const modulos = {
            cocina: false,
            easyRewards: false,
            restaurante: false,
            reserva: false
        }

        hotelQueryData?.hotel?.suscripciones?.forEach((sub) => {
            if (sub && !sub?.eliminado && sub?.estatus === "Activa") {
                sub?.modulos?.forEach((m) => {
                    if (m.nombre_modulo === "Cocina") modulos.cocina = true
                    if (m.nombre_modulo === "Easyrewards") modulos.easyRewards = true
                    if (m.nombre_modulo === "Restaurante") modulos.restaurante = true
                    if (m.nombre_modulo === "Reservas") modulos.reserva = true
                })
            }
        })

        dispatch(setModulos(modulos))
    }

    useEffect(() => {
        if (!hotelQueryData) return

        // Ya hay módulos reales: no hacen falta más reintentos.
        stopPolling()
        dispatch(toggleImasStatusAvailable(!hotelQueryData?.hotel.imas_transaccion_status))
        handleModulos()
        dispatch(setHotelLoading(false))
    }, [hotelQueryData])

    useEffect(() => {
        // Si la query falla se apaga el loading igual, porque si no la barra de navegación
        // quedaría oculta el resto de la sesión. Hasta que un reintento traiga los módulos
        // el menú cae al base (sin Reservas ni Restaurante).
        if (error) {
            dispatch(setHotelLoading(false))
        }
    }, [error])

    useEffect(() => {
        if (hotelQueryData || !hotel_id) return

        // Reintento inmediato al volver la conexión o la pestaña al frente, sin esperar al
        // siguiente poll.
        const retry = () => {
            if (document.visibilityState !== "visible") return
            // El error ya se maneja arriba vía `error`; aquí solo evita el rechazo suelto.
            refetch().catch(() => undefined)
        }

        document.addEventListener("visibilitychange", retry)
        window.addEventListener("online", retry)
        return () => {
            document.removeEventListener("visibilitychange", retry)
            window.removeEventListener("online", retry)
        }
    }, [hotelQueryData, hotel_id, refetch])

    useEffect(() => {
        // // si imas_transaccion_status es false el servicio está disponible, de lo contrario no
        dispatch(toggleImasStatusAvailable(!data?.checkHotel.imas_transaccion_status))
    }, [data])

    return children
}

export default HotelImasStatusProvider
